import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { TONE_FILLS } from '@/lib/tones'
import SectionLabel from '@/components/landing/section-label'
import { SERVICES } from '@/constants/landing'

const ServicesSection: FC = (): ReactNode => {
    return (
        <section id='services' aria-labelledby='services-title' className='bg-foreground text-background'>
            <div className='site-container py-24 md:py-36'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='03'>
                            Services
                        </SectionLabel>

                        <h2
                            id='services-title'
                            className='reveal mt-6 font-heading text-[clamp(3.25rem,10vw,10rem)] leading-[0.82] font-black uppercase wdth-75'
                        >
                            What we
                            <span className='block text-primary'>
                                make
                            </span>
                        </h2>
                    </div>

                    <p className='reveal text-lg leading-relaxed text-background/75 lg:col-span-5 lg:text-xl'>
                        Five disciplines under one roof, so strategy, design and renders never get lost in a handover. Hire us for one, or let us run the whole launch.
                    </p>
                </div>

                <ol className='mt-14 border-t border-background/25 md:mt-20'>
                    {SERVICES.map((service, index) => (
                        <li key={service.title} className='group relative overflow-hidden border-b border-background/25'>
                            <span
                                aria-hidden='true'
                                className={cn(
                                    'absolute inset-0 origin-bottom scale-y-0 transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-y-100 motion-reduce:transition-none',
                                    TONE_FILLS[service.tone]
                                )}
                            />

                            <div className='relative grid gap-5 py-8 transition-colors duration-300 group-hover:text-foreground md:py-10 lg:grid-cols-12 lg:items-center lg:gap-8 lg:px-4'>
                                <span className='font-mono text-sm font-medium lg:col-span-1'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h3 className='font-heading text-[clamp(2.75rem,6vw,6rem)] leading-[0.88] font-black uppercase wdth-75 transition-[font-variation-settings] duration-500 ease-out group-hover:wdth-100 motion-reduce:transition-none lg:col-span-6'>
                                    {service.title}
                                </h3>

                                <div className='lg:col-span-5'>
                                    <p className='text-base leading-relaxed text-background/75 transition-colors duration-300 group-hover:text-foreground md:text-lg'>
                                        {service.description}
                                    </p>

                                    <ul aria-label={`${service.title} deliverables`} className='mt-4 flex flex-wrap gap-2'>
                                        {service.deliverables.map(deliverable => (
                                            <li
                                                key={deliverable}
                                                className='rounded-full border border-current/40 px-3 py-1 text-xs font-semibold tracking-[0.08em] uppercase'
                                            >
                                                {deliverable}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}

export default ServicesSection
